import { useEffect, useState } from "react";
import axios from "axios";
import { Printer, Download } from "lucide-react";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { Switch } from "../ui/switch";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { withPdfPreviewParams } from "./pdfPreview";
import { canNativeSaveFile, saveOssBlob } from "../../lib/oss/bridge";
import { canNativePrint, printOssPdf } from "../../lib/oss/print";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LEGENDE_PER_TIPO = {
  schema_alvo: "Legenda: P = poco, T = tanto, D = diarrea, X = normale",
  schema_docce: "Legenda: M = mattina, P = pomeriggio, N = notte",
  haccp: "Legenda: valori espressi in °C — segnare la temperatura rilevata per ogni giorno",
  haccp_pulizie: "Legenda: segnare SI o NO per ogni area sanificata; firmare a fine giornata",
};

export default function PrintPreviewModal({
  open,
  onOpenChange,
  doc,
  token,
  onPrinted,
  customUrl,
  apiBase = "/api/stampe-mensili",
}) {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [formato, setFormato] = useState("a4");
  const [orientamento, setOrientamento] = useState("orizzontale");
  const [copie, setCopie] = useState("1");
  const [intestazione, setIntestazione] = useState(true);
  const [legenda, setLegenda] = useState(true);

  useEffect(() => {
    if (!open || !doc || !token) {
      setPdfUrl(null);
      return undefined;
    }
    let objectUrl = null;
    const url = customUrl
      || `${API_URL}${apiBase}/${doc.print_type}/file/${doc.file_id}`;
    axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: "blob",
    }).then((res) => {
      objectUrl = URL.createObjectURL(res.data);
      setPdfUrl(objectUrl);
    }).catch(() => setPdfUrl(null));
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [open, doc, token, customUrl, apiBase]);

  // Reset impostazioni di stampa quando si apre un nuovo documento
  useEffect(() => {
    if (open) {
      setFormato("a4");
      setOrientamento("orizzontale");
      setCopie("1");
      setIntestazione(true);
      setLegenda(true);
    }
  }, [open]);

  if (!doc) return null;

  const legendaText = LEGENDE_PER_TIPO[doc.print_type];

  // Apre il PDF come documento a sé stante in una nuova scheda e ne richiama
  // la stampa nativa del browser. È il metodo più affidabile: il visualizzatore
  // PDF integrato pagina correttamente l'intero documento (a differenza di un
  // <iframe> annidato in una pagina HTML, che spesso produce stampe vuote o
  // troncate alla sola porzione visibile a schermo).
  const handlePrint = async () => {
    if (!pdfUrl) return;
    // App OSS nativa: niente schede/popup nella WebView, stampa col servizio di sistema
    if (canNativePrint("pdf")) {
      try {
        const blob = await (await fetch(pdfUrl)).blob();
        if (await printOssPdf(blob, doc.file_name || "Stampa")) onPrinted?.();
      } catch {
        /* resta il download */
      }
      return;
    }
    const w = window.open(pdfUrl, "_blank");
    if (!w) return;
    let printed = false;
    const tryPrint = () => {
      if (printed) return;
      printed = true;
      try { w.focus(); w.print(); } catch { /* la scheda potrebbe essere ancora in caricamento */ }
    };
    w.addEventListener("load", tryPrint);
    // fallback per browser che non emettono "load" sul viewer PDF nativo
    setTimeout(tryPrint, 1200);
    onPrinted?.();
  };

  const handleDownload = async () => {
    if (!pdfUrl) return;
    if (canNativeSaveFile()) {
      const blob = await (await fetch(pdfUrl)).blob();
      await saveOssBlob(blob, doc.file_name || "documento.pdf");
      return;
    }
    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = doc.file_name || "documento.pdf";
    link.click();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[95vh] p-0 gap-0 overflow-hidden">
        <div className="flex h-[85vh]">
          <div className="flex min-w-0 flex-1 flex-col">
            <DialogHeader className="border-b px-6 py-4">
              <DialogTitle>Stampa — {doc.file_name || "Documento"}</DialogTitle>
            </DialogHeader>
            <div className="min-h-0 flex-1 bg-slate-100">
              {pdfUrl ? (
                <iframe
                  src={withPdfPreviewParams(pdfUrl)}
                  title="Anteprima PDF"
                  className="h-full w-full"
                  style={{ border: "none" }}
                />
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-slate-500">Caricamento anteprima…</div>
              )}
            </div>
            <DialogFooter className="border-t px-6 py-3">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
              <Button variant="outline" onClick={handleDownload} disabled={!pdfUrl}>
                <Download className="mr-2 h-4 w-4" />Scarica PDF
              </Button>
              <Button className="bg-[#00A86B] hover:bg-[#00915d]" onClick={handlePrint} disabled={!pdfUrl}>
                <Printer className="mr-2 h-4 w-4" />Stampa
              </Button>
            </DialogFooter>
          </div>

          <aside className="hidden w-72 shrink-0 border-l bg-white p-4 lg:block">
            <h3 className="mb-4 text-sm font-semibold text-slate-800">Impostazioni di stampa</h3>
            <div className="space-y-4 text-sm">
              <div>
                <Label className="text-xs text-slate-500">Formato carta</Label>
                <Select value={formato} onValueChange={setFormato}>
                  <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="a4">A4</SelectItem>
                    <SelectItem value="a3">A3</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Orientamento</Label>
                <Select value={orientamento} onValueChange={setOrientamento}>
                  <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="orizzontale">Orizzontale</SelectItem>
                    <SelectItem value="verticale">Verticale</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Copie</Label>
                <Select value={copie} onValueChange={setCopie}>
                  <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5].map((n) => (
                      <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-xs">Intestazione</Label>
                <Switch checked={intestazione} onCheckedChange={setIntestazione} />
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-xs">Legenda</Label>
                <Switch
                  checked={legenda}
                  onCheckedChange={setLegenda}
                  disabled={!legendaText}
                />
              </div>
              {!legendaText && (
                <p className="text-[11px] text-slate-400">Nessuna legenda disponibile per questo documento.</p>
              )}
              <p className="border-t pt-3 text-[11px] text-slate-400">
                Formato, orientamento e copie possono essere confermati o modificati nella finestra di stampa del browser.
              </p>
            </div>
          </aside>
        </div>
      </DialogContent>
    </Dialog>
  );
}
